import flatten from 'lodash/flatten'
import get from 'lodash/get'

import type { ShortlistSection, ShortlistTrackItem } from 'types/shortlist'
import type { Track } from 'types/track'

/** Sort direction for track ordering. */
export type SortDirection = 'asc' | 'desc'

/** Available sort keys for track sorting. */
export type SortKey =
  | 'track_title'
  | 'artist'
  | 'date_added'
  | 'year'
  | 'tracknumber'

const getDirectionValues = (direction: SortDirection) => {
  const lessThan = direction === 'asc' ? -1 : 1
  const moreThan = lessThan * -1
  return [lessThan, moreThan]
}

const valueComparator = (
  a: ShortlistTrackItem,
  b: ShortlistTrackItem,
  direction: SortDirection,
  valueAccessor: (track: Track) => any,
) => {
  const aValue = valueAccessor(a.track)
  const bValue = valueAccessor(b.track)
  // tracks without metadata value are always at the end, regardless of sort direction
  if (!aValue && bValue) return 1
  if (aValue && !bValue) return -1

  const [lessThan, moreThan] = getDirectionValues(direction)
  if (aValue < bValue) return lessThan
  if (aValue > bValue) return moreThan
  return 0
}

const nameComparator = (
  a: ShortlistTrackItem,
  b: ShortlistTrackItem,
  direction: SortDirection,
) =>
  valueComparator(a, b, direction, track =>
    track.name ? track.name.toLowerCase() : null,
  )

const trackNumberComparator = (
  a: ShortlistTrackItem,
  b: ShortlistTrackItem,
  direction: SortDirection,
) =>
  valueComparator(a, b, direction, track =>
    get(track, 'metadata.TrackNumber', null),
  )

const yearComparator = (
  a: ShortlistTrackItem,
  b: ShortlistTrackItem,
  direction: SortDirection,
) =>
  valueComparator(a, b, direction, track => get(track, 'metadata.Year', null))

const artistComparator = (
  a: ShortlistTrackItem,
  b: ShortlistTrackItem,
  direction: SortDirection,
) =>
  valueComparator(a, b, direction, track =>
    track.artist ? track.artist.toLowerCase() : null,
  )

const dateAddedComparator = (
  a: ShortlistTrackItem,
  b: ShortlistTrackItem,
  direction: SortDirection,
) =>
  valueComparator(a, b, direction, track => new Date(track.created).getTime())

/*
const idComparator = (a, b, direction: SortDirection) =>
  valueComparator(a, b, direction, track => track.id)
*/

const sortByComparator = (
  items: ShortlistTrackItem[],
  direction: SortDirection,
  comparator: any,
): ShortlistTrackItem[] => {
  const result = items.slice().sort((a, b) => comparator(a, b, direction))

  return result
}

/**
 * Sorts an array of shortlist track items by the specified criteria.
 *
 * Supports sorting by various track properties with ascending or descending order.
 * Tracks without the specified metadata are placed at the end regardless of direction.
 *
 * @param items - Array of shortlist track items to sort
 * @param key - The property to sort by
 * @param direction - Sort direction ('asc' or 'desc')
 * @returns New sorted array of track items
 *
 * @example
 * ```typescript
 * const sortedByTitle = sortItems(tracks, 'track_title', 'asc')
 * const sortedByDate = sortItems(tracks, 'date_added', 'desc')
 * const sortedByArtist = sortItems(tracks, 'artist', 'asc')
 * ```
 */
export const sortItems = (
  items: ShortlistTrackItem[],
  key: SortKey,
  direction: SortDirection,
): ShortlistTrackItem[] => {
  switch (key) {
    case 'track_title':
      return sortByComparator(items, direction, nameComparator)
    case 'artist':
      return sortByComparator(items, direction, artistComparator)
    case 'date_added':
      return sortByComparator(items, direction, dateAddedComparator)
    case 'year':
      return sortByComparator(items, direction, yearComparator)
    case 'tracknumber':
      return sortByComparator(items, direction, trackNumberComparator)
    default:
      return items
  }
}

/**
 * Sorts track items within their respective sections.
 *
 * When a shortlist has sections (like album groupings), this function sorts
 * tracks within each section separately, maintaining the section boundaries.
 *
 * @param sections - Array of section definitions with positions
 * @param items - Array of all track items
 * @param key - The property to sort by
 * @param direction - Sort direction ('asc' or 'desc')
 * @returns Flattened array of sorted track items maintaining section structure
 *
 * @example
 * ```typescript
 * const sections = [
 *   { position: 0, title: 'Album 1' },
 *   { position: 5, title: 'Album 2' }
 * ]
 *
 * const sortedWithSections = sortItemsInSections(
 *   sections,
 *   allTracks,
 *   'tracknumber',
 *   'asc'
 * )
 * // Tracks 0-4 sorted by track number, then tracks 5+ sorted by track number
 * ```
 */
export const sortItemsInSections = (
  sections: ShortlistSection[],
  items: ShortlistTrackItem[],
  key: SortKey,
  direction: SortDirection,
): ShortlistTrackItem[] => {
  const sectionItems = sections.map((section, index) => {
    if (index === sections.length - 1) return items.slice(section.position)
    return items.slice(section.position, sections[index + 1]?.position)
  })

  // insert items without sections
  if (sections[0]?.position && sections[0].position > 0) {
    sectionItems.unshift(items.slice(0, sections[0].position))
  }

  return flatten(sectionItems.map(si => sortItems(si, key, direction)))
}
